// src/lib/schema/helpers/formatters.js

/**
 * Convert schema object to JSON-LD string
 * @param {object} schema - Schema object
 * @returns {string} JSON-LD string
 */
export function schemaToString(schema) {
  if (!schema) return '{}';

  // Ensure schema has @context if it doesn't already
  const schemaWithContext = schema['@context'] 
    ? schema 
    : { '@context': 'https://schema.org', ...schema };

  return JSON.stringify(schemaWithContext);
}

/**
 * Create a combined schema with multiple elements
 * @param {array} schemas - Array of schema objects
 * @returns {object} Combined schema object
 */
export function combineSchemasToGraph(schemas) {
  // Filter out any null schemas
  const validSchemas = schemas.filter(schema => schema !== null);

  // Return schema or @graph of multiple schemas
  if (validSchemas.length === 0) {
    return {};
  } else if (validSchemas.length === 1) {
    return validSchemas[0];
  } else {
    return {
      '@context': 'https://schema.org',
      '@graph': validSchemas
    };
  }
}

/**
 * Create a combined schema with multiple elements as a string
 * @param {array} schemas - Array of schema objects
 * @returns {string} JSON-LD string
 */
export function combineSchemasToString(schemas) {
  return schemaToString(combineSchemasToGraph(schemas));
}

/**
 * Format price for schema (ensure it's a number with 2 decimal places)
 * @param {number|string} price - Price value
 * @returns {string} Formatted price
 */
export function formatPrice(price) {
  if (price === undefined || price === null) return "0.00";

  const numericPrice = Number(price);
  if (isNaN(numericPrice)) return "0.00";

  return numericPrice.toFixed(2);
}

/**
 * Format availability for schema
 * @param {boolean} isInStock - Whether product is in stock
 * @returns {string} Schema.org availability URL
 */
export function formatAvailability(isInStock) {
  return isInStock
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';
}

/**
 * Format title with proper capitalization
 * @param {string} title - Title to format
 * @returns {string} Formatted title
 */
export function formatTitle(title) {
  if (!title) return '';

  // Split by spaces and capitalize first letter of each word
  // except for articles, conjunctions, and prepositions
  const excludedWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'in', 'of'];

  return title.toLowerCase().split(' ').map((word, index) => {
    if (index === 0 || !excludedWords.includes(word)) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    }
    return word;
  }).join(' ');
}

/**
 * Build a URL with search parameters
 * @param {string} basePath - Base path
 * @param {object} searchParams - Search parameters
 * @param {string} baseUrl - Base URL
 * @param {string} lang - Language code
 * @returns {string} Complete URL
 */
export function buildUrl(basePath, searchParams = {}, baseUrl = 'https://www.yourcarpet.com', lang = 'en') {
  // Create URL search params
  const urlSearchParams = new URLSearchParams();

  // Add each search param
  Object.entries(searchParams).forEach(([key, value]) => {
    if (value) {
      urlSearchParams.append(key, value);
    }
  });

  // Build the path
  const searchParamsString = urlSearchParams.toString();
  const fullPath = searchParamsString
    ? `${basePath}?${searchParamsString}`
    : basePath;

  // Return the complete URL
  return `${baseUrl}/${lang}${fullPath}`;
}

/**
 * Clean an object by removing null or undefined values
 * @param {object} obj - Object to clean
 * @returns {object} Cleaned object
 */
export function cleanObject(obj) {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        // If value is an object, clean it recursively
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          return [key, cleanObject(value)];
        }
        return [key, value];
      })
  );
}
